'use client'

import { cn } from '@brew/ui/cn'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  adoptMeasuredEmailWidth,
  parseEmailHtmlContentWidth,
} from '@/lib/rendering/email-html-width'
import {
  computeFillLayoutWidth,
  computeScaledHeight,
  computeScaleToFit,
} from './email-jsx-preview-scaler'
import { PixelPerfectEmailFrame } from './pixel-perfect-email-frame'

const DEFAULT_DESIGN_WIDTH = 600

export interface ScaledEmailHtmlFrameProps {
  /** The exact persisted/delivered HTML document. */
  html: string
  title: string
  /**
   * When true (default), render the document at its own design width and
   * downscale-only via `transform: scale(...)` so it fits the host without
   * reflowing — the frame's internal viewport stays at the email's true
   * width, so the document's media queries never see the host's width.
   * Set to false for hosts that size the frame themselves.
   */
  fitToWidth?: boolean
  /**
   * With `fitToWidth`: when the host column is wider than the email, lay
   * the document out at the column's width instead of centering it at its
   * design width — the email's own body background then fills the column,
   * the way an email client shows it. Never upscales.
   */
  fillWidth?: boolean
  /**
   * Override the design width. When omitted it is parsed from the HTML
   * (`parseEmailHtmlContentWidth`) with a 600px fallback.
   */
  intrinsicWidth?: number
  /** Outer wrapper className. */
  className?: string
  /** Outer wrapper style overrides (merged with the computed wrapper style). */
  style?: CSSProperties
  /**
   * Height reserved until the frame reports its first measurement, so the
   * host layout doesn't collapse-then-jump while the document loads.
   */
  unmeasuredMinHeight?: number
  /**
   * Observes the resolved design width (parsed guess, then any live
   * measurement adopted over it) so hosts can size their own chrome.
   */
  onIntrinsicWidthResolved?: (width: number) => void
}

/**
 * Read-only, fit-to-width presentation of a stored email HTML document.
 *
 * Composition wrapper only: the document itself renders through an
 * untouched `PixelPerfectEmailFrame` (hardened srcDoc, self-measured
 * height). This component pins the frame to the email's design width and
 * scales the result down to the host column — the HTML twin of
 * `EmailJsxPreview`'s `fitToWidth` behavior.
 */
export function ScaledEmailHtmlFrame({
  html,
  title,
  fitToWidth = true,
  fillWidth = false,
  intrinsicWidth: explicitWidth,
  className,
  style,
  unmeasuredMinHeight,
  onIntrinsicWidthResolved,
}: ScaledEmailHtmlFrameProps) {
  const fallbackDesignWidth = useMemo(() => {
    if (explicitWidth != null && explicitWidth > 0) {
      return explicitWidth
    }
    return parseEmailHtmlContentWidth(html) ?? DEFAULT_DESIGN_WIDTH
  }, [explicitWidth, html])

  // Live measurement adopted over the parsed guess, keyed to the document it
  // was measured from — this component does not remount per document, so an
  // adoption must never survive an `html` swap.
  const [measured, setMeasured] = useState<{
    html: string
    width: number
  } | null>(null)
  const adoptedWidth = measured?.html === html ? measured.width : null
  const designWidth =
    explicitWidth != null && explicitWidth > 0
      ? explicitWidth
      : (adoptedWidth ?? fallbackDesignWidth)

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [parentWidth, setParentWidth] = useState<number>(0)
  const [contentHeight, setContentHeight] = useState<number>(0)
  // The latest column width, for the frame's width report: the frame keeps
  // the callback it was given at load, so a closed-over width goes stale
  // when the column resizes. Written where the width is measured.
  const parentWidthRef = useRef(0)

  // The frame reports scrollWidth at the pinned viewport — an up-only
  // overflow signal. Adoption re-pins the frame at the wider width, after
  // which the next report equals the adopted value and settles.
  const handleContentWidthChange = useCallback(
    (reportedWidth: number) => {
      // A filled frame's viewport is the column, so a report no wider than
      // the column is the viewport itself, not the email overflowing it.
      if (fillWidth && reportedWidth <= Math.floor(parentWidthRef.current)) {
        return
      }
      setMeasured((current) => {
        const active = current?.html === html ? current.width : null
        const base = active ?? fallbackDesignWidth
        const adopted = adoptMeasuredEmailWidth(reportedWidth, base)
        if (adopted === base) {
          return current
        }
        return { html, width: adopted }
      })
    },
    [html, fallbackDesignWidth, fillWidth]
  )

  useEffect(() => {
    onIntrinsicWidthResolved?.(designWidth)
  }, [designWidth, onIntrinsicWidthResolved])

  // Same measurement discipline as `EmailJsxPreview`: `offsetWidth` for the
  // initial read (layout box, unaffected by the scale transform), then
  // `ResizeObserver.contentRect` for updates.
  const observeWrapper = useCallback((node: HTMLDivElement | null) => {
    wrapperRef.current = node
    if (!node) {
      return
    }
    parentWidthRef.current = node.offsetWidth
    setParentWidth(node.offsetWidth)
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) {
        return
      }
      parentWidthRef.current = entry.contentRect.width
      setParentWidth(entry.contentRect.width)
    })
    ro.observe(node)
    return () => ro.disconnect()
  }, [])

  // Unscaled document height, reported by the frame's own measurement
  // (collapse → scrollHeight → settle). The wrapper reserves the scaled
  // equivalent because the transformed frame is out of layout flow.
  const handleHeightChange = useCallback((height: number) => {
    setContentHeight(height)
  }, [])

  const layoutWidth = fillWidth
    ? computeFillLayoutWidth({ parentWidth, intrinsicWidth: designWidth })
    : designWidth

  const { scale } = useMemo(
    () =>
      fitToWidth
        ? computeScaleToFit({ parentWidth, intrinsicWidth: layoutWidth })
        : { scale: 1, fits: true },
    [fitToWidth, parentWidth, layoutWidth]
  )

  const wrapperStyle = useMemo<CSSProperties>(() => {
    const merged: CSSProperties = {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      ...style,
    }
    if (contentHeight > 0) {
      merged.height = computeScaledHeight(contentHeight, scale)
    } else if (unmeasuredMinHeight != null) {
      merged.minHeight = unmeasuredMinHeight
    }
    return merged
  }, [style, contentHeight, scale, unmeasuredMinHeight])

  const scalerStyle = useMemo<CSSProperties>(() => {
    if (!fitToWidth) {
      return { width: '100%' }
    }
    return {
      width: layoutWidth,
      // Center when the column is wider than the email; when scaled, the
      // layout box exceeds the parent so the margins collapse to zero and
      // the scaled visual fills the parent exactly.
      marginInline: 'auto',
      transformOrigin: 'top left',
      transform: scale === 1 ? undefined : `scale(${scale})`,
    }
  }, [fitToWidth, layoutWidth, scale])

  return (
    <div
      ref={observeWrapper}
      className={cn('scaled-email-html-frame', className)}
      style={wrapperStyle}
    >
      <div style={scalerStyle}>
        <PixelPerfectEmailFrame
          html={html}
          title={title}
          onHeightChange={handleHeightChange}
          onContentWidthChange={handleContentWidthChange}
        />
      </div>
    </div>
  )
}
